/**
 * ExtractionStatusPanel — RAG M-I1/M-I2: AC-I5b read-only extracted content panel
 *
 * Shows:
 *   - Extraction status badge (processing/ready/failed+reason)
 *   - Extracted text (from documents)
 *   - Per-image OCR results (M-I2, AC-I6)
 *   - Skipped image flags (AC-I7: budget exceeded / vision failed)
 *   - Document summary (M-I2, AC-I9)
 *   - Placeholders for images-inside-PDF OCR (M-I3) — not yet implemented
 *
 * Read-only — no edit/save path.
 * Hidden when the item has no uploaded text/image attachments.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, AlertCircle, Clock, CheckCircle2, ImageIcon, AlertTriangle, RefreshCw } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';

export type ExtractionStatus = 'processing' | 'ready' | 'failed' | 'skipped' | undefined;

export interface ImageOcrEntry {
  attachmentId: string;
  filename: string;
  status: 'ok' | 'skipped_budget' | 'skipped_failed';
  text: string | null;
  reason: string | null;
}

export interface SkippedImageEntry {
  filename: string;
  reason: string;
}

export interface ExtractionStatusPanelProps {
  /** The knowledge item's content JSONB (parsed as an object) */
  content: Record<string, any> | null | undefined;
  /** Evidence attachment IDs — used to decide if panel should render */
  evidenceAttachmentIds?: string[];
  /** S-1: called when the user clicks "Reprocess document". */
  onReprocess?: () => void;
  /** S-1: true while the reprocess mutation is in-flight. */
  isReprocessing?: boolean;
}

function StatusBadge({ status }: { status: ExtractionStatus }) {
  if (!status || status === 'skipped') return null;

  if (status === 'processing') {
    return (
      <Badge variant="outline" className="flex items-center gap-1 text-yellow-700 border-yellow-300 bg-yellow-50">
        <Clock className="w-3 h-3" />
        {t('Processing')}
      </Badge>
    );
  }
  if (status === 'ready') {
    return (
      <Badge variant="outline" className="flex items-center gap-1 text-green-700 border-green-300 bg-green-50">
        <CheckCircle2 className="w-3 h-3" />
        {t('Ready')}
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="outline" className="flex items-center gap-1 text-red-700 border-red-300 bg-red-50">
        <AlertCircle className="w-3 h-3" />
        {t('Failed')}
      </Badge>
    );
  }
  return null;
}

export function ExtractionStatusPanel({ content, evidenceAttachmentIds, onReprocess, isReprocessing }: ExtractionStatusPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  const attachmentIds = evidenceAttachmentIds ?? (Array.isArray(content?.evidenceAttachmentIds) ? content.evidenceAttachmentIds : []);

  // Don't render if there are no attachments
  if (!attachmentIds || attachmentIds.length === 0) {
    return null;
  }

  const extractionStatus: ExtractionStatus = content?.extractionStatus as ExtractionStatus;
  const extractedText: string | undefined = typeof content?.extractedText === 'string' ? content.extractedText : undefined;
  const extractionError: string | undefined = typeof content?.extractionError === 'string' ? content.extractionError : undefined;
  // Strip the [SUMMARY] tag prefix before display (it's used for chunk tagging, not UI)
  const rawSummary: string | undefined = typeof content?.extractionSummary === 'string' ? content.extractionSummary : undefined;
  const extractionSummary: string | undefined = rawSummary?.replace(/^\[SUMMARY\]\s*/i, '');
  const imageOcrResults: ImageOcrEntry[] = Array.isArray(content?.extractionImageOcr) ? content.extractionImageOcr : [];
  // AC-I8: images embedded INSIDE documents (DOCX word/media/* + PDF XObjects)
  const embeddedImageOcrResults: ImageOcrEntry[] = Array.isArray(content?.extractionEmbeddedImageOcr)
    ? content.extractionEmbeddedImageOcr
    : [];
  const skippedImages: SkippedImageEntry[] = Array.isArray(content?.extractionSkippedImages) ? content.extractionSkippedImages : [];
  // AC-I10/I11/I12: enrichment status
  const enrichmentStatus: string | undefined = typeof content?.enrichmentStatus === 'string' ? content.enrichmentStatus : undefined;
  const enrichmentLanguage: string | undefined = typeof content?.enrichmentLanguage === 'string' ? content.enrichmentLanguage : undefined;

  // Hide if extraction was skipped (no extractable attachments) or hasn't started
  if (extractionStatus === 'skipped' || !extractionStatus) {
    return null;
  }

  const successfulOcr = imageOcrResults.filter((r) => r.status === 'ok' && r.text);

  return (
    <Card className="mb-4" data-testid="extraction-status-panel">
      <CardContent className="p-4">
        {/* Collapsible header */}
        <button
          type="button"
          className="flex items-center gap-2 w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
          onClick={() => setIsOpen((o) => !o)}
          aria-expanded={isOpen}
          aria-controls="extraction-panel-content"
          data-testid="extraction-panel-toggle"
        >
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
          )}
          <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="font-medium text-sm text-gray-700">{t('Extracted content')}</span>
          <div className="ml-2">
            <StatusBadge status={extractionStatus} />
          </div>
          {/* Read-only indicator + S-1 Reprocess action */}
          <span className="text-xs text-gray-400">{t('Read-only')}</span>
          {onReprocess && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto -mr-1 text-xs gap-1"
              onClick={(e) => { e.stopPropagation(); onReprocess(); }}
              disabled={isReprocessing || extractionStatus === 'processing'}
              loading={isReprocessing}
              data-testid="knowledge-reprocess-button"
              title={t('Re-run document extraction and embedding')}
            >
              <RefreshCw className="w-3 h-3" />
              {t('Reprocess')}
            </Button>
          )}
        </button>

        {/* Collapsible body */}
        {isOpen && (
          <div id="extraction-panel-content" className="mt-4 space-y-4">
            {/* Failure reason */}
            {extractionStatus === 'failed' && extractionError && (
              <Alert variant="error" className="text-sm">
                <AlertCircle className="w-4 h-4" />
                <span className="ml-2">
                  {t('Extraction failed')}: {extractionError}
                </span>
              </Alert>
            )}

            {/* Processing state */}
            {extractionStatus === 'processing' && (
              <div className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-3">
                {t('Document extraction is in progress. Check back shortly.')}
              </div>
            )}

            {extractionStatus === 'ready' && (
              <>
                {/* AC-I10/I12: Enrichment flag — enriched / not-enriched (budget) */}
                {enrichmentStatus && (
                  <div
                    className={`text-xs rounded px-2 py-1 inline-flex items-center gap-1 ${
                      enrichmentStatus === 'enriched'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                    }`}
                    data-testid="enrichment-status-badge"
                  >
                    {enrichmentStatus === 'enriched'
                      ? t('Enriched') + (enrichmentLanguage ? ` (${enrichmentLanguage})` : '')
                      : t('Indexed without enrichment — budget')}
                  </div>
                )}

                {/* Document summary (AC-I9) */}
                {extractionSummary && (
                  <div className="space-y-1" data-testid="extraction-summary">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('Summary')}</p>
                    <div className="text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded p-3 italic">
                      {extractionSummary}
                    </div>
                  </div>
                )}

                {/* Extracted text */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('Extracted text')}</p>
                  {extractedText ? (
                    <div
                      className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded border border-gray-200 p-3 max-h-64 overflow-y-auto font-mono leading-relaxed"
                      data-testid="extraction-text-content"
                      aria-readonly="true"
                    >
                      {extractedText}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">{t('No text was extracted from the document.')}</p>
                  )}
                </div>

                {/* Per-image OCR results (AC-I6 — M-I2) */}
                {successfulOcr.length > 0 && (
                  <div className="space-y-2" data-testid="ocr-results">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" />
                      {t('Image text (OCR)')}
                    </p>
                    {successfulOcr.map((ocr) => (
                      <div key={ocr.attachmentId} className="rounded border border-gray-200 p-3 bg-gray-50">
                        <p className="text-xs text-gray-400 mb-1">{ocr.filename}</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">{ocr.text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Skipped images (AC-I7: budget/vision failed) */}
                {skippedImages.length > 0 && (
                  <div className="space-y-1" data-testid="skipped-images">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-yellow-500" />
                      {t('Skipped images')}
                    </p>
                    {skippedImages.map((img, idx) => (
                      <div key={idx} className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                        <span className="font-medium">{img.filename}</span>
                        {img.reason && <span className="ml-1 text-yellow-600">— {img.reason}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* AC-I8: Images embedded inside documents (DOCX word/media + PDF XObjects) */}
                {embeddedImageOcrResults.filter((r) => r.status === 'ok' && r.text).length > 0 && (
                  <div className="space-y-2" data-testid="embedded-image-ocr-results">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" />
                      {t('Embedded image text (from document)')}
                    </p>
                    {embeddedImageOcrResults.filter((r) => r.status === 'ok' && r.text).map((ocr, idx) => (
                      <div key={idx} className="rounded border border-gray-200 p-3 bg-gray-50">
                        <p className="text-xs text-gray-400 mb-1">{ocr.filename}</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">{ocr.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                {embeddedImageOcrResults.filter((r) => r.status !== 'ok').length > 0 && (
                  <div className="space-y-1" data-testid="embedded-image-skipped">
                    {embeddedImageOcrResults.filter((r) => r.status !== 'ok').map((r, idx) => (
                      <div key={idx} className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                        <span className="font-medium">{r.filename}</span>
                        {r.reason && <span className="ml-1 text-yellow-600">— {r.reason}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {embeddedImageOcrResults.length === 0 && (
                  <div className="border border-dashed border-gray-200 rounded p-3 text-xs text-gray-400 italic" data-testid="pdf-embedded-images-placeholder">
                    {t('No embedded images found in uploaded documents.')}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
